import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlusCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface AddClientDialogProps {
  onClientAdded: (client: any) => void;
}

export function AddClientDialog({ onClientAdded }: AddClientDialogProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPosition, setContactPosition] = useState("");
  const [status, setStatus] = useState("active");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error("Nome do cliente é obrigatório");
      return;
    }
    
    try {
      setIsSubmitting(true);
      console.log("Starting client creation process");
      
      // Get user ID and company info
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error("Auth error:", userError);
        toast.error("Usuário não autenticado");
        setIsSubmitting(false);
        return;
      }

      console.log("Creating client with user_id:", user.id);

      // Get user's company_id
      const { data: userData, error: userDataError } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (userDataError) {
        console.error("Error fetching user data:", userDataError);
        toast.error("Erro ao buscar dados do usuário");
        setIsSubmitting(false);
        return;
      }

      let companyId = userData?.company_id;

      // Se não tem company_id, verificar se é colaborador
      if (!companyId) {
        const { data: collaboratorData } = await supabase
          .from('collaborators')
          .select('company_id')
          .eq('collaborator_id', user.id)
          .eq('status', 'active')
          .maybeSingle();

        if (collaboratorData) {
          companyId = collaboratorData.company_id;
          console.log('User is collaborator, using company:', companyId);
        }
      }

      if (!companyId) {
        console.error("User has no company_id");
        toast.error("Usuário não está associado a uma empresa");
        setIsSubmitting(false);
        return;
      }

      console.log("User company_id:", companyId);
      
      // Set logo initials
      const logoInitials = name.substring(0, 2).toUpperCase();
      console.log("Logo initials:", logoInitials);

      try {
        // Create client with company_id for proper visibility
        const { data, error } = await supabase
          .from("clients")
          .insert({
            name,
            email: email || null,
            phone: phone || null,
            address: address || null,
            contact_name: contactName || null,
            contact_position: contactPosition || null,
            logo_initials: logoInitials,
            status,
            user_id: user.id,
            company_id: companyId
          })
          .select();

        if (error) {
          console.error("Error creating client:", error);
          toast.error(`Erro ao adicionar cliente: ${error.message || "Erro desconhecido"}`);
          setIsSubmitting(false);
          return;
        }

        console.log("Client creation response:", data);

        if (!data || data.length === 0) {
          toast.error("Nenhum dado retornado após inserção do cliente");
          setIsSubmitting(false);
          return;
        }

        // Create kanban board for this client with proper company association
        const { error: kanbanError } = await supabase
          .from("kanban_boards")
          .insert({
            client_id: data[0].id,
            user_id: user.id,
            company_id: companyId,
            name: `Fluxo de Trabalho: ${name}`,
            description: `Quadro de tarefas para ${name}`
          });

        if (kanbanError) {
          console.error("Error creating kanban board:", kanbanError);
          toast.error(`Erro ao criar quadro Kanban: ${kanbanError.message}`);
        } else {
          // Get the board ID to create default columns
          const { data: boardData } = await supabase
            .from("kanban_boards")
            .select("id")
            .eq("client_id", data[0].id)
            .order("created_at", { ascending: false })
            .limit(1);

          if (boardData && boardData.length > 0) {
            // Create default columns
            await supabase
              .from("kanban_columns")
              .insert([
                { board_id: boardData[0].id, name: "A Fazer", position: 1 },
                { board_id: boardData[0].id, name: "Em Progresso", position: 2 },
                { board_id: boardData[0].id, name: "Em Revisão", position: 3 },
                { board_id: boardData[0].id, name: "Concluído", position: 4 }
              ]);
          }
        }

        toast.success(`Cliente ${name} adicionado com sucesso!`);
        
        // Notify parent component
        onClientAdded(data[0]);
        
        // Reset form and close dialog
        setName("");
        setEmail("");
        setPhone("");
        setAddress("");
        setContactName("");
        setContactPosition("");
        setStatus("active");
        setOpen(false);
      } catch (insertError: any) {
        console.error("Exception during client insert:", insertError);
        toast.error(`Erro ao inserir cliente no banco: ${insertError.message || "Erro desconhecido"}`);
      }
    } catch (error: any) {
      console.error("Exception when adding client:", error);
      toast.error(`Erro ao adicionar cliente: ${error.message || "Erro desconhecido"}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusCircle className="mr-2 h-4 w-4" />
          Novo Cliente
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Adicionar Novo Cliente</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Nome da empresa *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="organization"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Ativo</SelectItem>
                  <SelectItem value="inactive">Inativo</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Telefone</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
              />
            </div>
            
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="address">Endereço</Label>
              <Input
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                autoComplete="street-address"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contactName">Nome do contato</Label>
              <Input
                id="contactName"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                autoComplete="name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contactPosition">Cargo do contato</Label>
              <Input
                id="contactPosition"
                value={contactPosition}
                onChange={(e) => setContactPosition(e.target.value)}
                autoComplete="organization-title"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setOpen(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting || !name.trim()}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvando...
                </>
              ) : "Adicionar Cliente"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
